import React from 'react';
import { ConfigClient, ConfigCollection } from '@atlaskit/web-config-client';

import { FeatureFlags, getDynamicConfigOptions } from '@/utils/feature-flags';

import useOrgUserController from './org-user-controller';

type FeatureGatesContextType = {
  allowLowTraffic?: boolean;
  allowMimirSLO?: boolean;
  allowSimulation?: boolean;
};

export const FeatureGatesContext = React.createContext<FeatureGatesContextType>({});

const useFeatureGatesController = () => {
  const { orgId } = useOrgUserController();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [config, setConfig] = React.useState<ConfigCollection | null>(null);

  React.useEffect(() => {
    if (isInitialized || !orgId) return;

    const initializeGates = async () => {
      try {
        const dynamicConfig: ConfigCollection = await ConfigClient.fetch(
          getDynamicConfigOptions(orgId)
        );
        setConfig(dynamicConfig);
      } catch (_e) {
        // Feature flag fetching can fail in local dev due to CORS restrictions
        // on the remote Atlassian flags service. The app will continue with all
        // flags defaulting to false.
      } finally {
        setIsInitialized(true);
      }
    };
    initializeGates();
  }, [isInitialized, orgId]);

  return React.useMemo(() => {
    if (!isInitialized) {
      return {};
    }

    return {
      allowMimirSLO: (config?.getBoolean(FeatureFlags.AllowMimirSLO) as any)?.value || false,
      allowSimulation: (config?.getBoolean(FeatureFlags.AllowSimulation) as any)?.value || false,
    };
  }, [isInitialized, config]);
};

export default useFeatureGatesController;
